import type { NodeOptions } from '@unifyapps/network/generated/models/nodeOptions';
import type { TransformerConfig } from '@unifyapps/network/generated/models/transformerConfig';
import type { UIFilter } from '@unifyapps/network/generated/models/uIFilter';
import { useMemo } from 'react';
import type { GetComputeContextBuilder } from '../../../hooks/computeContext';
import type { UseGetNoCodeComputeDataType } from '../../../hooks/computeContext/useGetNoCodeComputedData';
import { useGetNoCodeComputedData } from '../../../hooks/computeContext/useGetNoCodeComputedData';
import { useDSCGetComputeContext } from '../context/selectors';

class NodeOptionsBuilder {
  private readonly options: NodeOptions;
  private readonly getComputeContext: GetComputeContextBuilder;
  private readonly getNoCodeComputedData: UseGetNoCodeComputeDataType['getNoCodeComputedData'];

  constructor({
    initialOptions = {},
    getComputeContext,
    getNoCodeComputedData,
  }: {
    initialOptions: NodeOptions;
    getComputeContext: GetComputeContextBuilder;
    getNoCodeComputedData: UseGetNoCodeComputeDataType['getNoCodeComputedData'];
  }) {
    this.options = { ...initialOptions };
    this.getComputeContext = getComputeContext;
    this.getNoCodeComputedData = getNoCodeComputedData;
  }

  withTransformer({ transformerConfig }: { transformerConfig: TransformerConfig }) {
    this.options.transformerConfig = transformerConfig;
    return this;
  }

  withCacheConfig(config: { filter?: UIFilter; cacheKey?: string; enabled?: boolean }) {
    if (!config.enabled) {
      return this;
    }

    const computedFilter = this.getNoCodeComputedData<{
      filter?: UIFilter;
    }>({ filter: config.filter }, this.getComputeContext());
    const computedCacheKey = this.getNoCodeComputedData<{
      cacheKey?: string;
    }>({ cacheKey: config.cacheKey }, this.getComputeContext());

    if (computedFilter.filter || computedCacheKey.cacheKey) {
      this.options.cacheConfig = {
        filter: computedFilter.filter,
        cacheKey: computedCacheKey.cacheKey,
      };
    }
    return this;
  }

  build() {
    return this.options;
  }
}

export function useNodeOptionsBuilder(params: { options?: Partial<NodeOptions> } | undefined) {
  const getComputeContext = useDSCGetComputeContext();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();

  return useMemo(() => {
    const nodeOptionsBuilder = new NodeOptionsBuilder({
      initialOptions: params?.options || {},
      getComputeContext,
      getNoCodeComputedData,
    });

    if (params?.options?.transformerConfig) {
      nodeOptionsBuilder.withTransformer({
        transformerConfig: params.options.transformerConfig,
      });
    }

    if (params?.options?.cacheConfig) {
      nodeOptionsBuilder.withCacheConfig({
        enabled: params.options.cacheConfig.enabled,
        filter: params.options.cacheConfig.filter,
        cacheKey: params.options.cacheConfig.cacheKey,
      });
    }

    return nodeOptionsBuilder.build();
  }, [params?.options, getComputeContext, getNoCodeComputedData]);
}
